import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  MessageSquare,
  Flag,
} from "lucide-react";

const Contact: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    urgency: "normal",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Netlify Forms: use default form submission
  const handleSubmit = (e: React.FormEvent) => {
    // Set the flag so ContactSuccess knows the form was submitted
    sessionStorage.setItem("contactFormSubmitted", "true");
    setIsSubmitting(true);
  };

  const isFormValid = () => {
    return (
      formData.name && formData.email && formData.subject && formData.message
    );
  };

  const contactInfo = [
    {
      icon: <Mail size={24} />,
      title: "Email",
      value: "odessyweb@gmail.com",
      href: "mailto:odessyweb@gmail.com",
    },
    // {
    //   icon: <Phone size={24} />,
    //   title: "Phone",
    //   value: "+27 (0) 12 345 6789",
    //   href: "tel:+27123456789",
    // },
    {
      icon: <MapPin size={24} />,
      title: "Location",
      value: "Worldwide - Remote Services",
      href: null,
    },
    {
      icon: <Clock size={24} />,
      title: "Business Hours",
      value: (
        <div style={{ lineHeight: "1.6" }}>
          <div>
            <span style={{ color: "#9C02CB", fontWeight: "bold" }}>SAST</span>:
            <span style={{ fontWeight: "bold" }}> Mon-Fri</span> 7:00-10:00 PM,{" "}
            <span style={{ fontWeight: "bold" }}>Weekends</span> 8:00 AM-10:00
            PM
          </div>
          <div>
            <span style={{ color: "#9C02CB", fontWeight: "bold" }}>EST</span>:
            <span style={{ fontWeight: "bold" }}> Mon-Fri</span> 12:00-3:00 PM,{" "}
            <span style={{ fontWeight: "bold" }}>Weekends</span> 1:00 AM-3:00 PM
          </div>
          <div>
            <span style={{ color: "#9C02CB", fontWeight: "bold" }}>GMT</span>:
            <span style={{ fontWeight: "bold" }}> Mon-Fri</span> 5:00-8:00 PM,{" "}
            <span style={{ fontWeight: "bold" }}>Weekends</span> 6:00 AM-8:00 PM
          </div>
        </div>
      ),
      href: null,
    },
  ];

  return (
    <div>
      {/* Header Section */}
      <section
        className="hero"
        style={{ paddingTop: "2rem", paddingBottom: "2rem" }}
      >
        <div className="hero-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>
              Contact <span className="gradient-text">OWS</span>
            </h1>
            <p>
              Get in touch with us to discuss your project or ask any questions
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="section" ref={ref}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h2 className="section-title">
              Get in <span className="gradient-text">Touch</span>
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "2rem",
                marginBottom: "3rem",
              }}
            >
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  className="card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  style={{ textAlign: "center" }}
                >
                  <div style={{ color: "#9C02CB", marginBottom: "1rem" }}>
                    {info.icon}
                  </div>
                  <h3>{info.title}</h3>
                  {info.href ? (
                    <a
                      href={info.href}
                      target={info.href.startsWith("http") ? "_blank" : "_self"}
                      rel={
                        info.href.startsWith("http")
                          ? "noopener noreferrer"
                          : ""
                      }
                      style={{
                        color: "#666",
                        textDecoration: "none",
                        transition: "color 0.3s ease",
                      }}
                      onMouseEnter={(e) =>
                        ((e.target as HTMLElement).style.color = "#9C02CB")
                      }
                      onMouseLeave={(e) =>
                        ((e.target as HTMLElement).style.color = "#666")
                      }
                    >
                      {info.value}
                    </a>
                  ) : (
                    <div style={{ color: "#666" }}>{info.value}</div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section
        id="send-message-section"
        className="section"
        style={{ background: "#f9fafb" }}
      >
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h2 className="section-title">
              Send us a <span className="gradient-text">Message</span>
            </h2>

            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
                style={{
                  maxWidth: "600px",
                  margin: "0 auto",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1))",
                    border: "2px solid #10b981",
                    borderRadius: "20px",
                    padding: "3rem 2rem",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                    style={{ fontSize: "4rem", marginBottom: "1rem" }}
                  >
                    🎉
                  </motion.div>

                  <motion.h3
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    style={{
                      color: "#065f46",
                      marginBottom: "1rem",
                      fontSize: "1.8rem",
                      fontWeight: "bold",
                    }}
                  >
                    Message Sent Successfully!
                  </motion.h3>

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    style={{
                      fontSize: "1.1rem",
                      color: "#047857",
                      lineHeight: "1.6",
                      marginBottom: "2rem",
                    }}
                  >
                    Thank you for contacting OWS! 🚀 <br />
                    We've received your message and will get back to you within
                    24 hours.
                  </motion.p>

                  <motion.button
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    onClick={() => {
                      setIsSubmitted(false);
                    }}
                    style={{
                      background: "linear-gradient(135deg, #10b981, #059669)",
                      color: "white",
                      border: "none",
                      borderRadius: "50px",
                      padding: "0.75rem 2rem",
                      fontSize: "1rem",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 6px 20px rgba(16, 185, 129, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 15px rgba(16, 185, 129, 0.3)";
                    }}
                  >
                    ✨ Send Another Message
                  </motion.button>
                </div>
              </motion.div>
            ) : (
              <form
                name="contact"
                method="POST"
                data-netlify="true"
                action="/contact-success"
                className="form-container"
                onSubmit={handleSubmit}
              >
                {/* Netlify hidden fields */}
                <input type="hidden" name="form-name" value="contact" />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                    gap: "1rem",
                  }}
                >
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">
                      <Phone size={16} style={{ marginRight: "0.5rem" }} />
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="urgency" className="form-label">
                      <Flag size={16} style={{ marginRight: "0.5rem" }} />
                      Priority Level
                    </label>
                    <select
                      id="urgency"
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="low">Low Priority</option>
                      <option value="normal">Normal</option>
                      <option value="high">High Priority</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject" className="form-label">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Brief description of your inquiry"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    <MessageSquare
                      size={16}
                      style={{ marginRight: "0.5rem" }}
                    />
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Tell us about your project requirements, goals, and any key features you need..."
                    required
                    style={{ height: "150px" }}
                  />
                </div>

                {/* Error message display removed */}

                <button
                  type="submit"
                  className="form-button"
                  disabled={!isFormValid() || isSubmitting}
                  style={{
                    background: isSubmitting
                      ? "linear-gradient(135deg, #6b7280, #9ca3af)"
                      : "linear-gradient(135deg, #9C02CB, #d946ef)",
                    transform: isSubmitting ? "scale(0.98)" : "scale(1)",
                    transition: "all 0.3s ease",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Send size={20} style={{ marginRight: "0.5rem" }} />
                  Send Message
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Social Links & Additional Info */}
      {/* <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "3rem",
                alignItems: "center",
              }}
            >
              <div>
                <h2
                  className="section-title"
                  style={{ textAlign: "left", marginBottom: "1.5rem" }}
                >
                  Connect with <span className="gradient-text">Blessing</span>
                </h2>
                <p
                  style={{
                    fontSize: "1.1rem",
                    color: "#666",
                    lineHeight: "1.7",
                    marginBottom: "2rem",
                  }}
                >
                  Follow my journey and stay updated with the latest projects,
                  insights, and industry trends. Let's connect and build
                  something amazing together!
                </p>
                <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                  {socialLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        padding: "0.75rem 1.5rem",
                        background: "white",
                        border: "2px solid #e5e7eb",
                        borderRadius: "50px",
                        color: "#666",
                        textDecoration: "none",
                        transition: "all 0.3s ease",
                        fontSize: "0.9rem",
                        fontWeight: "500",
                      }}
                      onMouseEnter={(e) => {
                        const target = e.target as HTMLElement;
                        target.style.borderColor = "#9C02CB";
                        target.style.color = "#9C02CB";
                        target.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        const target = e.target as HTMLElement;
                        target.style.borderColor = "#e5e7eb";
                        target.style.color = "#666";
                        target.style.transform = "translateY(0)";
                      }}
                    >
                      {link.icon}
                      {link.name}
                      <ExternalLink size={14} />
                    </a>
                  ))}
                </div>
              </div>

              <div
                style={{
                  background:
                    "linear-gradient(135deg, #9C02CB 0%, #d946ef 100%)",
                  padding: "2rem",
                  borderRadius: "15px",
                  color: "white",
                  textAlign: "center",
                }}
              >
                <h3 style={{ marginBottom: "1rem" }}>Ready to Start?</h3>
                <p style={{ marginBottom: "1.5rem", opacity: 0.9 }}>
                  Don't wait! Let's discuss your project and turn your ideas
                  into reality.
                </p>
                <a
                  href="/booking"
                  className="cta-button"
                  style={{
                    background: "white",
                    color: "#9C02CB",
                    display: "inline-block",
                  }}
                >
                  Book Free Consultation
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section> */}

      {/* FAQ Section */}
      <section className="section" style={{ background: "#f9fafb" }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h2 className="section-title">
              Frequently Asked <span className="gradient-text">Questions</span>
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "2rem",
              }}
            >
              <div className="card">
                <h3 style={{ color: "#9C02CB", marginBottom: "1rem" }}>
                  What's your response time?
                </h3>
                <p>
                  We typically respond to all inquiries within 24 hours during
                  business days. Urgent requests are prioritized and handled
                  even faster. Please note our business hours are in South
                  African Standard Time (SAST).
                </p>
              </div>
              <div className="card">
                <h3 style={{ color: "#9C02CB", marginBottom: "1rem" }}>
                  Do you offer ongoing support?
                </h3>
                <p>
                  Yes! We provide ongoing maintenance and support services for
                  all our projects. We're here to help you succeed long after
                  launch.
                </p>
              </div>
              {/* <div className="card">
                <h3 style={{ color: "#9C02CB", marginBottom: "1rem" }}>
                  What technologies do you use?
                </h3>
                <p>
                  We specialize in modern web technologies including React,
                  TypeScript, Node.js, and various databases. We choose the best
                  tools for each project.
                </p>
              </div> */}
              <div className="card">
                <h3 style={{ color: "#9C02CB", marginBottom: "1rem" }}>
                  How do you handle project pricing?
                </h3>
                <p>
                  We provide custom quotes based on your specific needs and
                  requirements. Contact us for a detailed proposal tailored to
                  your project.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
